import React from 'react';
import styled from '@emotion/styled';
import { PortableText, type PortableTextBlockComponent, type PortableTextProps } from '@portabletext/react';

import { H4Styles, AStyles, PStyles } from '@/components/type';
import { COLORS, FONT_WEIGHT, SPACE } from '@/constants';

const RichTextH2 = styled.h2`
  ${H4Styles};
  letter-spacing: normal;
  margin-top: ${SPACE.X2};
  margin-bottom: calc(${SPACE.X1} + ${SPACE.X0});
`;

const RichTextAnchor = styled.a`
  ${AStyles};
  color: ${COLORS.BRAND_BLUE_EIGHT};
  font-weight: ${FONT_WEIGHT.BOLD};
  text-decoration: underline;
`;

const RichTextFigure = styled.figure`
  margin: ${SPACE.X2} 0;
  max-width: 100%;
`;

const RichTextImg = styled.img`
  margin: ${SPACE.X2} 0;
  max-width: 100%;
`;

const RichTextUl = styled.ul`
  ${PStyles};
  list-style-type: disc;
  padding-inline-start: ${SPACE.X2};
  margin-top: ${SPACE.X0};
  margin-bottom: ${SPACE.X1};
`;

const RichTextOl = styled.ol`
  ${PStyles};
  list-style-type: decimal;
  padding-inline-start: ${SPACE.X2};
  margin-top: ${SPACE.X0};
  margin-bottom: ${SPACE.X1};
`;

const RichTextLi = styled.li`
  list-style-position: outside;
  margin-bottom: ${SPACE.X0};
  padding-left: ${SPACE.X0};
`;

const RichTextNormal = styled.p<{ noSpacing?: boolean }>`
  ${PStyles};
`;

const RichTextNormalNoSpacing: PortableTextBlockComponent = ({ children }) => (
  <RichTextNormal noSpacing>{children}</RichTextNormal>
);

type RichTextProps = {
  portableTextValue?: PortableTextProps['value'];
  noSpacing?: boolean;
};

const RichText = ({ portableTextValue = [], noSpacing = false }: RichTextProps) => {
  return (
    <PortableText
      components={{
        list: {
          bullet: RichTextUl,
          number: RichTextOl,
        },
        listItem: {
          bullet: ({ children }) => <RichTextLi>{children}</RichTextLi>,
          number: ({ children }) => <RichTextLi>{children}</RichTextLi>,
        },
        block: {
          h2: RichTextH2,
          figure: RichTextFigure,
          img: RichTextImg,
          normal: noSpacing ? RichTextNormalNoSpacing : RichTextNormal,
        },
        marks: {
          link: ({ children, value }) => <RichTextAnchor href={value?.href}>{children}</RichTextAnchor>,
        },
      }}
      value={portableTextValue}
    />
  );
};

export default RichText;
